import {useTranslation} from '@aboutyou/i18n';
import {useHistory, useOrdersOverviewRoute} from '@aboutyou/router';
import {
  GenericIconName,
  Overlay,
  OverlayArrow,
  Z_INDEXES,
  useHydrationDone,
  useMatchesMediaQuery,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {useAuthenticationPrompt} from '../../../../features/Authentication/AuthenticationProvider/AuthenticationProvider';
import {useIsAuthenticated} from '../../../../features/Authentication/hooks/useIsAuthenticated';
import {useIsHintCovered} from '../../../../features/ShopHint/hooks/useIsHintCovered';
import {IconMetaNavigationItem} from '../IconMetaNavigationItem';
import {EnableSettingsModalProps, FlyoutLoggedIn} from './FlyoutLoggedIn';
import {FlyoutLoggedOut} from './FlyoutLoggedOut';

export const IconMetaNavigationItemUser: React.FC<{
  enableSettingsModalComponent: (
    props: EnableSettingsModalProps,
  ) => React.ReactNode;
  onClick: React.MouseEventHandler;
}> = ({enableSettingsModalComponent, onClick}) => {
  const {t} = useTranslation();
  const history = useHistory();
  const {isAuthenticated} = useIsAuthenticated();
  const supportsHover = useMatchesMediaQuery('(pointer: fine)');
  const ordersOverviewRoute = useOrdersOverviewRoute();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const hydrationDone = useHydrationDone();
  const [enableBankDataRequest, setEnableBankDataRequest] = useState(false);
  const {setIsHintCovered} = useIsHintCovered();

  return (
    <Container
      onMouseEnter={() => {
        setIsHintCovered(true);
        if (!enableBankDataRequest && isAuthenticated) {
          setEnableBankDataRequest(true);
        }
      }}
      onMouseLeave={() => {
        setIsHintCovered(false);
      }}
    >
      <IconMetaNavigationItem
        dataTestId="UserAccount"
        dataAttributes={{'data-dd-privacy': 'mask'}}
        icon={GenericIconName.NavUser}
        label={isAuthenticated ? t('Account') : t('Login')}
        onClick={event => {
          onClick(event);

          if (isAuthenticated) {
            if (supportsHover) {
              history.push(ordersOverviewRoute);
            }
          } else {
            showUserAuthenticationPrompt({includeCheckoutSession: false});
          }
        }}
      />
      {hydrationDone && (isAuthenticated || supportsHover) && (
        <>
          <StyledOverlayArrow />
          <OverlayPopup>
            <StyledOverlay>
              {isAuthenticated ? (
                <FlyoutLoggedIn
                  enableSettingsModalComponent={enableSettingsModalComponent}
                  enableBankDataRequest={enableBankDataRequest}
                  onLogout={() => setEnableBankDataRequest(false)}
                />
              ) : (
                <FlyoutLoggedOut />
              )}
            </StyledOverlay>
          </OverlayPopup>
        </>
      )}
    </Container>
  );
};

// Used to add padding around the overlay to not create a gap that
// would cause the overlay to close when the mouse leaves the trigger
const OverlayPopup = styled.div`
  display: none;
  position: absolute;
  top: calc(100% - 7px - 0.5rem);
  right: -0.5rem;
  z-index: ${Z_INDEXES.TADARIDA_HEADER_ICON_META_NAVIGATION_CONTENT};
  padding: 0.5rem;
`;

// Added explicitly to position it centered to the trigger
const StyledOverlayArrow = styled(OverlayArrow)`
  display: none;
  top: calc(100% - 8px - 0.5rem);
  z-index: ${Z_INDEXES.TADARIDA_HEADER_ICON_META_NAVIGATION_CONTENT + 1};
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  height: 100%;

  &:hover {
    ${OverlayPopup}, ${StyledOverlayArrow} {
      display: block;
    }
  }
`;

const StyledOverlay = styled(Overlay)`
  padding: 0;
`;
